import type { PersistedClient, Persister } from '@tanstack/react-query-persist-client';
import { del, get, set } from 'idb-keyval';

/**
 * Creates an Indexed DB persister for react-query.
 * Based on https://github.com/TanStack/query/discussions/3198
 *
 * If IndexedDB is unavailable, the persister silently degrades:
 * queries still work in-memory, they just won't persist across page loads.
 */
export function createReactQueryIDBPersister(input: {
    key: IDBValidKey;
    staleTime?: number;
}): Persister {
    const { key, staleTime } = input;
    let persistTimeoutId = 0;
    return {
        // The `persistClient` function may be called frequently (each time the cache is written),
        // but we don't need the data to be persisted synchronously.
        // So, we debounce it to avoid calling it too often.
        persistClient: (client: PersistedClient) => {
            window.clearTimeout(persistTimeoutId);
            persistTimeoutId = window.setTimeout(() => {
                set(key, client).catch(() => {
                    // Silently degrade, queries still work in-memory.
                });
            }, 1000);
        },
        restoreClient: async () => {
            try {
                const client = await get<PersistedClient>(key);

                // Make queries stale when restoring the client.
                if (client && staleTime !== undefined) {
                    client.clientState.queries.forEach((query) => {
                        query.state.dataUpdatedAt = Date.now() - staleTime;
                    });
                }

                return client;
            } catch {
                return;
            }
        },
        removeClient: async () => {
            window.clearTimeout(persistTimeoutId);
            try {
                await del(key);
            } catch {
                // Silently degrade, queries still work in-memory.
            }
        },
    } as Persister;
}
